import React, { useEffect, useState, useRef } from "react"
import { Editor } from "@tiptap/core"
import classNames from "classnames"
import { LinkIcon, XMarkIcon } from "@heroicons/react/24/outline"

const classNamesForButton = (isActive: boolean | undefined, extras?: string) =>
  classNames(
    "cursor-pointer py-1.5 text-center text-stone-600 transition-colors hover:bg-stone-900/[0.06] dark:text-stone-300 dark:hover:bg-white/[0.08]",
    extras,
    {
      "text-sky-600 dark:text-sky-400": isActive,
    }
  )

export default function FormattingMenu({ editor }: { editor: Editor }) {
  const [addingLink, setAddingLink] = useState(false)
  const linkInputRef = useRef<HTMLInputElement | null>(null)

  const bold = () => editor.chain().focus().toggleBold().run()
  const italic = () => editor.chain().focus().toggleItalic().run()
  const underline = () => editor.chain().focus().toggleUnderline().run()
  const strikethrough = () => editor.chain().focus().toggleStrike().run()
  const code = () => editor.chain().focus().toggleCode().run()

  useEffect(() => {
    if (addingLink)
      setTimeout(() => {
        linkInputRef.current?.focus()
      }, 50)
  }, [addingLink, linkInputRef])

  return (
    <div className="formatting-menu z-10 flex select-none items-stretch overflow-hidden rounded-lg border border-stone-900/10 bg-white/95 shadow-[0_8px_28px_rgba(15,23,42,0.14)] backdrop-blur-xl dark:border-white/10 dark:bg-gray-800/95 dark:shadow-[0_8px_28px_rgba(0,0,0,0.45)]">
      {!addingLink && (
        <>
          <div
            onClick={bold}
            className={classNamesForButton(
              editor.isActive("bold"),
              "w-8 font-extrabold"
            )}
          >
            B
          </div>
          <div
            onClick={italic}
            className={classNamesForButton(
              editor.isActive("italic"),
              "w-8 italic"
            )}
          >
            i
          </div>
          <div
            onClick={underline}
            className={classNamesForButton(
              editor.isActive("underline"),
              "w-8 underline"
            )}
          >
            U
          </div>
          <div
            onClick={strikethrough}
            className={classNamesForButton(
              editor.isActive("strike"),
              "w-8 line-through"
            )}
          >
            S
          </div>
          <div
            onClick={code}
            className={classNamesForButton(
              editor.isActive("code"),
              "w-8 font-mono"
            )}
          >
            &lt;&gt;
          </div>
          <div
            onClick={() => {
              setAddingLink(true)
              editor.chain().focus().run()
            }}
            className={classNamesForButton(
              editor.isActive("link"),
              "flex items-center border-l border-stone-200 px-2.5 text-xs dark:border-white/10"
            )}
          >
            <div>Add link</div>
          </div>
        </>
      )}
      {addingLink && (
        <div className="flex items-stretch border-l border-stone-200 text-xs dark:border-white/10">
          <div className="pointer-events-none flex w-8 items-center justify-center text-stone-400">
            <LinkIcon className="h-4 w-4" />
          </div>
          <input
            ref={linkInputRef}
            className="h-8 border-x border-stone-200 bg-transparent px-1.5 py-0.5 text-xs text-stone-800 placeholder:text-stone-400 focus:outline-none dark:border-white/10 dark:text-stone-100 dark:placeholder:text-stone-500"
            onKeyUp={(e) => {
              if (e.key === "Enter") {
                e.preventDefault()
                const url = e.currentTarget.value
                editor.chain().focus().setLink({ href: url }).run()
                setAddingLink(false)
              }
            }}
            onPaste={(e) => {
              e.preventDefault()
              const url = e.clipboardData.getData("text")
              editor.chain().focus().setLink({ href: url }).run()
              setAddingLink(false)
            }}
            placeholder="Paste link here"
          />
          <div
            onClick={() => {
              setAddingLink(false)
              editor.chain().focus().run()
            }}
            className="flex w-8 cursor-pointer items-center justify-center text-stone-400 transition-colors hover:bg-stone-900/[0.06] hover:text-stone-700 dark:hover:bg-white/[0.08] dark:hover:text-stone-200"
          >
            <XMarkIcon className="h-4 w-4" />
          </div>
        </div>
      )}
    </div>
  )
}
